import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MRT_DensityState,
  MRT_TableOptions,
  MRT_VisibilityState,
} from 'material-react-table';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, styled, useTheme } from '@mui/material';
import { IconCheck, IconLink } from '@tabler/icons-react';

import {
  Button,
  Checkbox,
  DateRangeFilterButton,
  IconButton,
  InfiniteTable,
  Order,
  Tooltip,
  Typography,
} from '@zendesk/forethought-elements';
import { DateRange } from '../../discover-dashboard-page/types';
import InsightDownloadCsv from '../common/InsightDownloadCsv';
import { initialInsightParams, INTENT_FILTER_PARAM_NAME } from '../constants';
import {
  dateRangeToTimestamp,
  isUiQueryError,
  normalizeFilters,
  sortDirectionToInsightSortDirection,
} from '../helpers';
import { useGetChannelOptionsForSolveInsights } from '../hooks/useGetChannelOptions';
import { useGetCurrentTab } from '../hooks/useGetCurrentTab';
import { useGetFilterCount } from '../hooks/useGetFilterCount';
import useGetWorkflowFilterOptions, {
  WorkflowFilterOption,
} from '../hooks/useGetWorkflowFilterOptions';
import useInsightCardViewInfo from '../hooks/useInsightCardViewInfo';
import useNormalizeChannels, {
  useNormalizeChannelsForBuildQueryFromMultiSelected,
} from '../hooks/useNormalizeChannels';
import {
  InsightPeriod,
  InsightSortDirection,
  Scope,
  SearchQueryMode,
} from '../types';
import ChatsTableLayout from './ChatsTableLayout';
import { buildConversationColumns, getTableHeight, View } from './helpers';
import merge from 'lodash/fp/merge';
import flagHeartIcon from 'src/assets/images/icon-flag-heart.svg';
import DebouncedSearchBar from 'src/components/debounced-search-bar/DebouncedSearchBar';
import GlobalFilterDrawer from 'src/components/global-filter-drawer';
import {
  listCVObjectDeserialize,
  listCVObjectSerialize,
  listEventObjectDeserialize,
  listEventObjectSerialize,
} from 'src/components/global-filter-drawer/helper';
import { ContextVariableFilterItemValue } from 'src/components/global-filter-drawer/types';
import useDiscoverSectionViewable from 'src/hooks/discover/useDiscoverSectionViewable';
import useOrgConfig from 'src/hooks/discover/useOrgConfig';
import {
  useEmitTrackingEventCallback,
  useGetWorkflowTags,
  useHasAiQaAccess,
  useIsFeatureFlagEnabled,
  useStateParams,
} from 'src/hooks/hooks';
import { useIsHumanEvalsEnabled } from 'src/hooks/useHumanEvalsGates';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import {
  ALLTIME_START,
  getConversationTypeFilterOptions,
  getDeflectionInsightFilterOptions,
  getInsightsFilterOptions,
  getResolutionTierFilterOptions,
  getSentimentInsightFilterOptions,
  getVoiceInsightsFilterOptions,
} from 'src/pages/intent-conversation-analytics/constants';
import ConversationDrawer from 'src/pages/intent-conversation-analytics/ConversationDrawer';
import { buildQueryFromMultiSelected } from 'src/pages/intent-conversation-analytics/hooks/useConversations';
import useGetCSATColor from 'src/pages/intent-conversation-analytics/hooks/useGetCSATColor';
import useIsDeflectionInsightsEnabled from 'src/pages/intent-conversation-analytics/hooks/useIsDeflectionInsightsEnabled';
import useIsProactiveConversationsEnabled from 'src/pages/intent-conversation-analytics/hooks/useIsProactiveConversationsEnabled';
import { useIsSentimentInsightsEnabled } from 'src/pages/intent-conversation-analytics/hooks/useIsSentimentInsightsEnabled';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';
import { EventChartProperty } from 'src/services/insight-cards/types';
import {
  useGetConversationQuery,
  useGetConversationTableQuery,
} from 'src/services/insights';
import {
  HUMAN_FEEDBACK_FILTER_VALUES,
  HumanFeedbackFilterValue,
  InsightConversation,
  InsightConversationSortColumns,
} from 'src/services/insights/types';
import { getWidgetConfiguration } from 'src/slices/solve-config/solveConfigSlice';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import type { ChannelMode } from 'src/types/workflowBuilderAPITypes';
import {
  dateRangeToTimeFilter,
  genericSerializeAndDeserialize,
  listDeserialize,
  listSerialize,
} from 'src/utils/discover/helpers';
import { ExportableTableType } from 'src/utils/enums';
import { datePickerRangeOptionsRevamp } from 'src/utils/timeRangeHelpers';

interface ChatsTableProps {
  abTestConfigId?: string;
  channel?: string;
  // Voice inbound/outbound mode tab; scopes the table + column set.
  channelMode?: ChannelMode;
  dateRange: DateRange;
  handleFilterReset?: () => void;
  initialMultiSelected?: string[];
  isApiChannel?: boolean;
  isDetail?: boolean;
  isSlackChannel?: boolean;
  isVoiceChannel?: boolean;
  isWebCallChannel?: boolean;
  period?: InsightPeriod;
  scope: Scope;
  scrollTop: number;
  scrollToTop: (yOffset: number, smooth?: boolean) => void;
  setDateRange: (
    dateRange: DateRange,
    batchUpdater?: () => URLSearchParams,
  ) => URLSearchParams;
  setPeriodicalFilter?: (period: InsightPeriod) => void;
  variantFilterOptions?: WorkflowFilterOption[];
  view: View;
  density?: MRT_DensityState;
}

const ChatsTable = ({
  abTestConfigId,
  channel,
  channelMode,
  dateRange,
  density = 'compact',
  handleFilterReset,
  initialMultiSelected = [],
  isApiChannel = false,
  isDetail,
  isSlackChannel = false,
  isVoiceChannel,
  isWebCallChannel = false,
  period,
  scope,
  scrollToTop,
  setDateRange,
  setPeriodicalFilter,
  variantFilterOptions,
  view,
}: ChatsTableProps) => {
  const { t } = useTranslation('solve-insights');
  const dispatch = useAppDispatch();
  const { articleId = undefined } = useParams<'articleId'>();
  const { topicId = undefined } = useParams<'topicId'>();
  const { tableTab = undefined } = useParams<'tableTab'>();
  const { workflowId = undefined } = useParams<'workflowId'>();
  const isParentTopic = location.pathname.includes('parent');
  const [page, setPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const getCSATColor = useGetCSATColor();
  const {
    human_evals_supported_channels: humanEvalsSupportedChannels = [],
    taxonomyVersion,
  } = useOrgConfig();
  const isV3Taxonomy = taxonomyVersion === 'V3';
  const isHumanEvalsEnabled = useIsHumanEvalsEnabled();
  // The main Conversations tab never passes `channel` (views are expressed as
  // mode booleans), so treat "no channel" as reviewable outside voice/web call.
  // Those views only contain review-enabled channels, the BE nulls the review
  // fields per-row for any that aren't, and super-admin QA must see the column
  // even if the org-config allowlist is missing — so don't gate on the list.
  const isReviewEnabledChannel = channel
    ? humanEvalsSupportedChannels.includes(channel)
    : !isVoiceChannel && !isWebCallChannel;
  const { palette } = useTheme();

  useEffect(() => {
    dispatch(getWidgetConfiguration());
  }, [dispatch]);

  const tab = useGetCurrentTab({
    articleId,
    isParentTopic,
    tableTab,
    topicId,
    workflowId,
  });
  const [cvFilters, setCvFilters] = useStateParams<
    ContextVariableFilterItemValue[]
  >({
    deserialize: listCVObjectDeserialize,
    initialState: [],
    paramsName: `${INTENT_FILTER_PARAM_NAME}_cv`,
    serialize: listCVObjectSerialize,
  });
  const [eventFilters, setEventFilters] = useStateParams<EventChartProperty[]>({
    deserialize: listEventObjectDeserialize,
    initialState: [],
    paramsName: `${INTENT_FILTER_PARAM_NAME}_event`,
    serialize: listEventObjectSerialize,
  });
  const [multiSelected, setMultiSelected] = useStateParams<string[]>({
    deserialize: listDeserialize,
    initialState: initialMultiSelected,
    paramsName: INTENT_FILTER_PARAM_NAME,
    serialize: listSerialize,
  });

  // Sync initialMultiSelected to URL params on mount so the GlobalFilterDrawer can read them
  const initialMultiSelectedRef = useRef(initialMultiSelected);
  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    if (
      initialMultiSelectedRef.current.length > 0 &&
      !currentParams.has(INTENT_FILTER_PARAM_NAME)
    ) {
      setMultiSelected(initialMultiSelectedRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deserializeSortDirection = useCallback(
    (param: string) => (param === 'ASC' ? 'ASC' : 'DESC'),
    [],
  );

  const deserializeSortColumn = useCallback(
    (param: string) => param as InsightConversationSortColumns,
    [],
  );

  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isQuickFeedbackEnabled = featureFlags.includes('quick_feedback');
  const isCustomMetricsEnabled = featureFlags.includes('custom_metrics');
  const isVoiceMultiBrandEnabled = featureFlags.includes('voice_multibrand');
  const isResolutionUiEnabled = featureFlags.includes('resolution_ui');
  const { discoverAnalyticsEnabled, showInternalBadgeForAnalytics } =
    useDiscoverSectionViewable();
  // state params
  const [sortDirection, setSortDirection] =
    useStateParams<InsightSortDirection>({
      deserialize: deserializeSortDirection,
      initialState: 'DESC',
      paramsName: 'chats_sort_direction',
      serialize: String,
    });
  const [searchQuery, setSearchQuery] = useStateParams<string>({
    deserialize: (str: string) => (str === 'null' ? '' : str),
    initialState: '',
    paramsName: 'chats_search',
    serialize: genericSerializeAndDeserialize,
  });
  const [searchQueryMode, setSearchQueryMode] = useStateParams<SearchQueryMode>(
    {
      deserialize: genericSerializeAndDeserialize as (
        str: string,
      ) => SearchQueryMode,
      initialState: 'conversation_id',
      paramsName: 'chats_search_mode',
      serialize: genericSerializeAndDeserialize,
    },
  );
  const [sortColumn, setSortColumn] =
    useStateParams<InsightConversationSortColumns>({
      deserialize: deserializeSortColumn,
      initialState: initialInsightParams.chatColumnSort,
      paramsName: 'chats_sort_column',
      serialize: String,
    });
  const [channels, setChannels] = useStateParams<string[]>({
    deserialize: listDeserialize,
    initialState: [],
    paramsName: 'channels',
    serialize: listSerialize,
  });
  const [humanFeedback, setHumanFeedback] = useStateParams<
    HumanFeedbackFilterValue[]
  >({
    deserialize: value =>
      listDeserialize(value).filter((v): v is HumanFeedbackFilterValue =>
        HUMAN_FEEDBACK_FILTER_VALUES.includes(v as HumanFeedbackFilterValue),
      ),
    initialState: [],
    paramsName: 'chats_human_feedback',
    serialize: listSerialize,
  });
  const [sourceRecommendationId] = useStateParams<string>({
    deserialize: genericSerializeAndDeserialize,
    initialState: '',
    paramsName: 'source_recommendation_id',
    serialize: genericSerializeAndDeserialize,
  });
  const [conversationIdsSubset] = useStateParams<string>({
    deserialize: genericSerializeAndDeserialize,
    initialState: '',
    paramsName: 'conversation_ids_subset',
    serialize: genericSerializeAndDeserialize,
  });
  // end state params

  // A non-empty, strict subset of the buckets is an active filter; selecting
  // all of them (or none) means "no human-feedback filter" — matches the
  // backend, which treats all-or-none as unfiltered. The standalone Flagged
  // toggle button is the shortcut for the "only flagged" selection, so it and
  // the drawer checkboxes share this single piece of state.
  const humanFeedbackKey = humanFeedback.join(',');
  const isHumanFeedbackFiltered =
    humanFeedback.length > 0 &&
    humanFeedback.length < HUMAN_FEEDBACK_FILTER_VALUES.length;
  const humanFeedbackParam = isHumanFeedbackFiltered
    ? humanFeedbackKey
    : undefined;
  const isFlaggedOnly =
    humanFeedback.length === 1 && humanFeedback[0] === 'flagged';

  // Reset page when the human-feedback selection changes (covers cross-component
  // toggling from the HumanFeedbackCard's "Give feedback" button, where the
  // toolbar's onClick page reset doesn't run).
  useEffect(() => {
    setPage(1);
  }, [humanFeedbackKey]);

  // Hooks
  const emitTrackingEventCallback = useEmitTrackingEventCallback();
  const workflowTags = useGetWorkflowTags();
  /**
   * Transient keystroke state lives in DebouncedSearchBar so typing never
   * re-renders the table; this component only sees the debounced, committed
   * `searchQuery` from the URL param.
   */
  const uiQueryError = isUiQueryError({
    searchQueryMode,
    uiQuery: searchQuery,
  });

  const isDeflectionInsightsEnabled = useIsDeflectionInsightsEnabled();
  const isSentimentInsightsEnabled = useIsSentimentInsightsEnabled();
  const isProactiveConversationsEnabled = useIsProactiveConversationsEnabled();

  // get conversation details once clicked
  const [selectedConversationId, setSelectedConversationId] = useStateParams<
    string | null
  >({
    deserialize: (param: string) => {
      return param || null;
    },
    initialState: null,
    paramsName: 'conversation_id',
    replace: true,
    serialize: (state: string | null) => {
      return state || '';
    },
  });

  const handleDrawerClose = () => {
    if (selectedConversationId) {
      emitTrackingEventCallback('insight-chat-drawer-close', {
        id: selectedConversationId,
        scope,
        tab,
      });
    }
    setSelectedConversationId(null);
  };

  const handleDrawerOpen = () => {
    if (!selectedConversationId) {
      return;
    }

    emitTrackingEventCallback('insight-chat-drawer-open', {
      id: selectedConversationId,
      scope,
      tab,
    });
  };

  const {
    data: conversationData,
    isFetching: isFetchingConversation,
    isLoading: isLoadingConversation,
  } = useGetConversationQuery(
    { conversation_id: selectedConversationId || '' },
    {
      skip: !selectedConversationId,
    },
  );

  const selectedConversation = selectedConversationId
    ? conversationData
    : undefined;
  // end get conversation details once clicked

  const channelOptions = useGetChannelOptionsForSolveInsights();
  const isVoiceOutboundEnabled = useIsFeatureFlagEnabled(
    'solve_voice_outbound',
  );
  const isSmsCsatEnabled = useIsFeatureFlagEnabled('voice_sms_csat');
  const isClassicWorkflowsDeprecated = useIsFeatureFlagEnabled(
    'classic_workflows_deprecated',
  );
  const hasAiQaPermission = useHasAiQaAccess();
  const backendReadyTimestamps = dateRangeToTimestamp(dateRange);

  const isSearchingByChatId =
    searchQueryMode === 'conversation_id' && searchQuery && !uiQueryError;
  // A deep-link from the Improved Policy card pins the table to one
  // recommendation's conversation set (resolved server-side from the id). Like
  // chat-id search it drops the workflow filter and uses the full date range,
  // since the experiment's conversations can predate the current window.
  const isFilteringByRecommendation = Boolean(sourceRecommendationId);
  // A deep-link from a review hunk's source modal pins the table to an
  // explicit set of conversation ids. Same drill-down semantics as the
  // recommendation filter: those conversations can predate any window.
  const isFilteringByConversationSubset = Boolean(conversationIdsSubset);
  const isDrillingIntoConversationSet =
    isFilteringByRecommendation || isFilteringByConversationSubset;
  const end = useMemo(() => Math.floor(Date.now() / 1000), []);

  const filterCount = useGetFilterCount({
    channels,
    cvFilters,
    eventFilters,
    isHumanFeedbackFiltered,
    multiSelected,
    workflowTags,
  });

  const handleResetAllFilter = useCallback(() => {
    setPeriodicalFilter && setPeriodicalFilter(initialInsightParams.period);
    handleFilterReset && handleFilterReset();
    emitTrackingEventCallback('insight-reset-filter', {
      scope,
      tab,
      table: 'chat',
    });

    setSearchQuery('');
    setHumanFeedback([]);

    setSearchQueryMode('conversation_id', () =>
      setSortColumn(initialInsightParams.chatColumnSort, () =>
        setDateRange(initialInsightParams.date, () =>
          setMultiSelected(initialMultiSelected, () =>
            setChannels([], () => setCvFilters([], () => setEventFilters([]))),
          ),
        ),
      ),
    );
  }, [
    setPeriodicalFilter,
    handleFilterReset,
    emitTrackingEventCallback,
    scope,
    tab,
    setSearchQuery,
    setHumanFeedback,
    setSearchQueryMode,
    setSortColumn,
    setDateRange,
    setMultiSelected,
    initialMultiSelected,
    setChannels,
    setCvFilters,
    setEventFilters,
  ]);

  const { cardId } = useParams<'cardId'>();
  const normalizeChannelsForBuildQueryFromMultiSelected =
    useNormalizeChannelsForBuildQueryFromMultiSelected();

  const normalizeChannels = useNormalizeChannels(true);

  const tableRequestParams = useMemo(() => {
    const selectedQuery = buildQueryFromMultiSelected(multiSelected);
    const builtQuery =
      isSearchingByChatId || isDrillingIntoConversationSet
        ? {
            conversation_status: selectedQuery.conversation_status,
          }
        : selectedQuery;
    const hasWorkflowVersionFilter = 'workflow_versions' in builtQuery;

    return normalizeFilters({
      article_id: articleId,
      channels: channel
        ? channels.length > 0
          ? buildQueryFromMultiSelected(
              normalizeChannelsForBuildQueryFromMultiSelected(channels),
            )?.channels
          : normalizeChannels([channel]).join(',')
        : tab === 'topic'
          ? buildQueryFromMultiSelected(channels)?.channels
          : buildQueryFromMultiSelected(
              normalizeChannelsForBuildQueryFromMultiSelected(channels),
            )?.channels,
      chat: searchQueryMode === 'chat' ? searchQuery : '',
      conversation_id:
        searchQueryMode === 'conversation_id' && !uiQueryError
          ? searchQuery
          : '',
      cv_filters: cvFilters,
      data_export_type: ExportableTableType.INSIGHT_CHATS_TABLE,
      event_filters: eventFilters,
      is_parent_topic: isParentTopic,
      sort_column: sortColumn,
      sort_direction: sortDirection,
      topic_id: topicId,
      user_query: searchQueryMode === 'user_query' ? searchQuery : '',
      widget_ticket_id:
        searchQueryMode === 'widget_ticket_id' ? searchQuery : '',
      workflow_id:
        hasWorkflowVersionFilter || isDrillingIntoConversationSet
          ? undefined
          : workflowId,
      ...(humanFeedbackParam ? { human_feedback: humanFeedbackParam } : {}),
      ...(sourceRecommendationId
        ? { source_recommendation_id: sourceRecommendationId }
        : {}),
      ...(conversationIdsSubset
        ? { conversation_ids_subset: conversationIdsSubset }
        : {}),
      ...(isSearchingByChatId
        ? // Use maximum date range:
          {
            end: end,
            page: 1,
            start: Number(ALLTIME_START),
          }
        : isDrillingIntoConversationSet
          ? // Conversation-set drill-down: full date range (the set's
            // conversations can predate the current window) with normal paging.
            {
              end,
              page,
              start: Number(ALLTIME_START),
            }
          : {
              end: backendReadyTimestamps.end_timestamp,
              page,
              start: backendReadyTimestamps.start_timestamp,
              ...builtQuery,
            }),
      ...(cardId && { chart_id: cardId }),
      // Voice mode tab drives channel_mode; wins over any stale Type filter.
      ...(isVoiceChannel && channelMode ? { channel_mode: channelMode } : {}),
    });
  }, [
    articleId,
    channel,
    channelMode,
    isVoiceChannel,
    normalizeChannels,
    tab,
    channels,
    normalizeChannelsForBuildQueryFromMultiSelected,
    searchQueryMode,
    searchQuery,
    uiQueryError,
    cvFilters,
    eventFilters,
    isParentTopic,
    sortColumn,
    sortDirection,
    topicId,
    workflowId,
    isSearchingByChatId,
    end,
    backendReadyTimestamps.end_timestamp,
    backendReadyTimestamps.start_timestamp,
    page,
    multiSelected,
    cardId,
    humanFeedbackParam,
    sourceRecommendationId,
    conversationIdsSubset,
    isDrillingIntoConversationSet,
  ]);

  const {
    data: conversationTableData,
    isError: isChatsTableError,
    isFetching: isConversationTableFetching,
    isLoading: isConversationTableLoading,
  } = useGetConversationTableQuery({
    ...tableRequestParams,
  });

  // Flagged-queue companion fetch: same filters, but always scoped to the
  // flagged-pending bucket regardless of the user's human-feedback selection.
  // Used by the feedback panel to walk between flagged conversations. The
  // explicit `human_feedback: 'flagged'` overrides any value inherited from
  // tableRequestParams (the backend lets human_feedback supersede). Only fired
  // when a conversation is selected so we don't pay for it on plain table views.
  const { data: flaggedConversationTableData } = useGetConversationTableQuery(
    { ...tableRequestParams, human_feedback: 'flagged', page: 1 },
    { skip: !selectedConversationId },
  );

  const flaggedConversationIds = useMemo<string[]>(
    () =>
      flaggedConversationTableData?.data.map(row => row.conversation_id) ?? [],
    [flaggedConversationTableData],
  );

  const rows = useMemo(() => {
    if (!conversationTableData?.data) {
      return [];
    }
    return conversationTableData.data;
  }, [conversationTableData]);

  // Extract unique AI QA rubric names from the data
  const aiQaRubricNames = useMemo(() => {
    if (!hasAiQaPermission || !rows.length) {
      return [];
    }
    const rubricNamesSet = new Set<string>();
    rows.forEach(row => {
      row.ai_qa_scores?.forEach(score => {
        rubricNamesSet.add(score.rubric_name);
      });
    });
    return Array.from(rubricNamesSet);
  }, [hasAiQaPermission, rows]);

  const columns = useMemo(
    () =>
      buildConversationColumns(
        t,
        palette,
        getCSATColor,
        emitTrackingEventCallback,
        dispatch,
        scope,
        tab,
        isCustomMetricsEnabled,
        channelOptions,
        isSlackChannel,
        isApiChannel,
        isDetail,
        isDeflectionInsightsEnabled,
        isQuickFeedbackEnabled,
        Boolean(isVoiceChannel),
        isSentimentInsightsEnabled,
        discoverAnalyticsEnabled,
        showInternalBadgeForAnalytics,
        isV3Taxonomy,
        isVoiceMultiBrandEnabled,
        isVoiceOutboundEnabled,
        isSmsCsatEnabled,
        hasAiQaPermission,
        aiQaRubricNames,
        abTestConfigId,
        isWebCallChannel,
        isHumanEvalsEnabled,
        isReviewEnabledChannel,
        channelMode,
        isResolutionUiEnabled,
        isProactiveConversationsEnabled,
      ),
    [
      t,
      palette,
      getCSATColor,
      emitTrackingEventCallback,
      dispatch,
      scope,
      tab,
      isCustomMetricsEnabled,
      channelOptions,
      isDetail,
      isSlackChannel,
      isApiChannel,
      isDeflectionInsightsEnabled,
      isQuickFeedbackEnabled,
      isVoiceChannel,
      isWebCallChannel,
      isSentimentInsightsEnabled,
      discoverAnalyticsEnabled,
      showInternalBadgeForAnalytics,
      isV3Taxonomy,
      isVoiceMultiBrandEnabled,
      isVoiceOutboundEnabled,
      isSmsCsatEnabled,
      hasAiQaPermission,
      aiQaRubricNames,
      abTestConfigId,
      isHumanEvalsEnabled,
      isReviewEnabledChannel,
      channelMode,
      isResolutionUiEnabled,
      isProactiveConversationsEnabled,
    ],
  );

  const showLoadingSkeleton =
    isConversationTableLoading || (page === 1 && isConversationTableFetching);

  const timeFilter = dateRangeToTimeFilter(dateRange);
  const initialTimeFilter = dateRangeToTimeFilter(initialInsightParams.date);

  const multiSelectedChanged =
    multiSelected.length !== initialMultiSelected.length ||
    multiSelected.some(item => !initialMultiSelected.includes(item));

  const areFiltersUsed = Boolean(
    searchQuery.length ||
    timeFilter.key !== initialTimeFilter.key ||
    multiSelectedChanged ||
    cvFilters.length ||
    eventFilters.length ||
    (period && period !== initialInsightParams.period) ||
    (channels.length && channels.length !== 3) ||
    isHumanFeedbackFiltered,
  );

  const getChatsTableDownloadUrl = useCallback(() => {
    const url = new URL(`${API_URL}data-export`);
    return url.href;
  }, []);

  const handleSortCallback = useCallback(
    (property: keyof InsightConversation, order: Order) => {
      setPage(1);
      setSortDirection(sortDirectionToInsightSortDirection(order));
      setSortColumn(
        property === 'useful_count'
          ? 'quick_feedback'
          : (property as InsightConversationSortColumns),
      );
      scrollToTop(72);
      emitTrackingEventCallback('insight-table-sort-change', {
        column: property,
        direction: order,
        scope,
        tab,
        table: 'chat',
      });
    },
    [
      tab,
      scrollToTop,
      emitTrackingEventCallback,
      scope,
      setSortColumn,
      setSortDirection,
    ],
  );

  const initialSorting = useMemo(
    () => [
      {
        desc: sortDirection === 'DESC',
        id: sortColumn,
      },
    ],
    [sortColumn, sortDirection],
  );

  const tableDataPage = conversationTableData?.metadata.page ?? 0;

  const handleLoadMore = useCallback(() => {
    if (tableDataPage === page) {
      const nextPage = page + 1;
      setPage(nextPage);
      emitTrackingEventCallback('insight-table-load-change', {
        currentPage: nextPage,
        scope,
        tab,
        table: 'chat',
      });
    }
  }, [tab, emitTrackingEventCallback, page, scope, tableDataPage]);

  const { fallbackWorkflowIds, options: intentsForFilter } =
    useGetWorkflowFilterOptions({
      end: backendReadyTimestamps.end_timestamp,
      start: backendReadyTimestamps.start_timestamp,
    });

  const { isInsightCardView, isMultipleChannels } = useInsightCardViewInfo();

  const filterOptions = useMemo(() => {
    const extraFilters = [];

    if (isInsightCardView && isMultipleChannels) {
      if (isSentimentInsightsEnabled) {
        extraFilters.push(...getSentimentInsightFilterOptions());
      }

      extraFilters.push({
        label: t('chatsTable.allAgents'),
        options: intentsForFilter,
        value: 'workflow_id',
      });

      return getInsightsFilterOptions({
        extraFilters,
        hideClassicWorkflowType: isClassicWorkflowsDeprecated,
        hideCSAT: true,
        hideQuickFeedback: true,
        hideWorkflowTypes: true,
        showConversationStatus: true,
      });
    }

    if (isVoiceChannel) {
      const voiceFilters = getVoiceInsightsFilterOptions({
        channelMode: channelMode === 'outbound' ? 'outbound' : 'inbound',
        isResolutionUiEnabled,
      });
      return [
        {
          label: t('chatsTable.allAgents'),
          options: intentsForFilter,
          value: 'workflow_id',
        },
        ...voiceFilters,
      ];
    }

    if (tab === 'workflow' && variantFilterOptions?.length) {
      extraFilters.push({
        label: t('chatsTable.allAgents'),
        options: variantFilterOptions,
        value: 'workflow_id',
      });
    } else if (tab !== 'workflow') {
      extraFilters.push({
        label: t('chatsTable.allAgents'),
        options: intentsForFilter,
        value: 'workflow_id',
      });
    }

    if (isResolutionUiEnabled) {
      extraFilters.push(
        ...getResolutionTierFilterOptions({ includeDeflected: true }),
      );
    }

    if (isProactiveConversationsEnabled) {
      extraFilters.push(...getConversationTypeFilterOptions());
    }

    if (isDeflectionInsightsEnabled) {
      extraFilters.push(...getDeflectionInsightFilterOptions());
    }

    if (isSentimentInsightsEnabled) {
      extraFilters.push(...getSentimentInsightFilterOptions());
    }

    return getInsightsFilterOptions({
      extraFilters,
      hideClassicWorkflowType: isClassicWorkflowsDeprecated,
      hideCSAT: isSlackChannel,
      hideDeflection: isResolutionUiEnabled,
      hideQuickFeedback: !isQuickFeedbackEnabled,
      hideWorkflowTypes: tab === 'workflow',
      showConversationStatus: true,
    });
  }, [
    t,
    isInsightCardView,
    isMultipleChannels,
    isVoiceChannel,
    isSlackChannel,
    tab,
    isDeflectionInsightsEnabled,
    isSentimentInsightsEnabled,
    isProactiveConversationsEnabled,
    isResolutionUiEnabled,
    intentsForFilter,
    channelMode,
    isClassicWorkflowsDeprecated,
    variantFilterOptions,
    isQuickFeedbackEnabled,
  ]);

  const searchOptions = useMemo(
    () => [
      { text: t('chatsTable.search.conversationId'), value: 'conversation_id' },
      { text: t('chatsTable.search.query'), value: 'user_query' },
      { text: t('chatsTable.search.conversation'), value: 'chat' },
      { text: t('chatsTable.search.ticketId'), value: 'widget_ticket_id' },
    ],
    [t],
  );

  const isGapsOnlySelected = fallbackWorkflowIds.some(id =>
    multiSelected.includes(`workflow_id.${id}`),
  );

  const rawLocalStorageOrgData = window.localStorage.getItem('org');
  const orgData = rawLocalStorageOrgData
    ? JSON.parse(rawLocalStorageOrgData)
    : null;
  const userRole = localStorage.getItem('userRole');

  const storageKey =
    userRole === 'SUPER_ADMIN' && orgData?.org_id
      ? `chatsTableOptions_org_${orgData.org_id}`
      : 'chatsTableOptions';

  const defaultPinnedLeft = useMemo(() => ['timestamp'], []);

  const defaultTableOptions = useMemo(() => {
    return {
      columnPinning: {
        left: defaultPinnedLeft,
        right: ['mrt-row-actions'],
      },
      columnVisibility: {
        time_spent_in_widget: false,
        voice_outbound_answered: false,
        voice_outbound_engaged: false,
        widget_ticket_id: false,
      },
      density,
    } as const satisfies MRT_TableOptions<InsightConversation>['initialState'];
  }, [defaultPinnedLeft, density]);

  const [savedTableOptions, setSavedTableOptions] = useLocalStorage<
    MRT_TableOptions<InsightConversation>['initialState']
  >(storageKey, defaultTableOptions);

  const defaultedSavedTableOptions = useMemo(
    () => merge(defaultTableOptions, savedTableOptions),
    [defaultTableOptions, savedTableOptions],
  );

  const pinnedLeftColumns = useMemo(() => {
    const savedPinnedLeft =
      savedTableOptions?.columnPinning?.left ?? defaultPinnedLeft;

    // Older local table state may have pinned this column before the final
    // column order was settled; keep only timestamp pinned on load.
    return savedPinnedLeft.filter(
      columnId => columnId !== 'resolution_automated',
    );
  }, [defaultPinnedLeft, savedTableOptions]);

  // Controlled state passed to InfiniteTable. Memoized so the spread on
  // `tableOptions` further down doesn't allocate a new state object on
  // every render — MRT would otherwise treat each render as a state
  // change and re-process column order/pinning unnecessarily.
  const chatsTableControlledState = useMemo(
    () => ({
      // Force MRT to follow the array order on every render so
      // review_status stays at the end of the unpinned section when the
      // async AI QA columns appear (otherwise MRT remembers its initial
      // position and floats it mid-table). Accessor columns expose their
      // id as `accessorKey` on the raw def, so we have to fall back to
      // that — `c.id` alone would drop every accessor column and
      // rearrange the row.
      columnOrder: columns
        .map(c => {
          const def = c as {
            accessorKey?: string;
            id?: string;
          };
          return def.id ?? def.accessorKey ?? '';
        })
        .filter(Boolean),
      columnPinning: {
        left: pinnedLeftColumns,
        right: ['mrt-row-actions'],
      },
      // Visibility + density are controlled (their onChange handlers write to
      // savedTableOptions); they must be fed back here or MRT never re-reads
      // them and the show/hide + density toggles appear frozen.
      columnVisibility: defaultedSavedTableOptions.columnVisibility,
      density: defaultedSavedTableOptions.density as MRT_DensityState,
    }),
    [columns, pinnedLeftColumns, defaultedSavedTableOptions],
  );

  const tableOptions = useMemo<MRT_TableOptions<InsightConversation>>(() => {
    const handleRowClick = (conversationId: string) => {
      setSelectedConversationId(conversationId);
      emitTrackingEventCallback('insight-table-row-clicked', {
        id: conversationId,
        scope,
        tab,
        table: 'chat',
      });
    };

    return {
      columns,
      data: rows,
      displayColumnDefOptions: {
        'mrt-row-actions': {
          header: '',
          muiTableBodyCellProps: {
            align: 'center',
          },
          muiTableHeadCellProps: {
            align: 'center',
          },
          size: 50,
        },
      },
      enableColumnActions: false,
      enableColumnFilters: false,
      enableExpanding: false,
      enableFullScreenToggle: false,
      enableGlobalFilter: false,
      enableRowActions: true,
      initialState: {
        ...defaultedSavedTableOptions,
        columnPinning: {
          ...defaultedSavedTableOptions.columnPinning,
        },
        density: defaultedSavedTableOptions.density as MRT_DensityState,
      },
      muiTableBodyRowProps: ({ row }) => ({
        onClick: () => handleRowClick(row.original.conversation_id),
      }),
      muiTopToolbarProps: { sx: { overflow: 'auto' } },
      onColumnPinningChange: updater => {
        const newPinning =
          typeof updater === 'function'
            ? updater(defaultedSavedTableOptions.columnPinning)
            : updater;
        setSavedTableOptions({
          ...defaultedSavedTableOptions,
          columnPinning: newPinning as { left: string[]; right: string[] },
        });
      },
      onColumnVisibilityChange: updater => {
        const newVisibility =
          typeof updater === 'function'
            ? updater(defaultedSavedTableOptions.columnVisibility)
            : updater;
        setSavedTableOptions({
          ...defaultedSavedTableOptions,
          columnVisibility: newVisibility as MRT_VisibilityState,
        });
      },
      onDensityChange: density => {
        setSavedTableOptions({
          ...defaultedSavedTableOptions,
          density: density as MRT_DensityState,
        });
      },
      renderRowActions: ({ row }) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip tooltipContent={t('chatsTable.copyLink')}>
            <IconButton
              // eslint-disable-next-line i18next/no-literal-string -- programmatic identifier, not display copy
              aria-label={'copy-conversation-id'}
              onClick={e => {
                e.stopPropagation();
                const conversationId = row.original.conversation_id;
                const url = new URL(window.location.href);
                url.searchParams.set('chats_search', conversationId);
                url.searchParams.set('conversation_id', conversationId);
                navigator.clipboard.writeText(url.toString());

                dispatch(
                  setGlobalToastOptions({
                    title: t('chatsTable.linkCopied'),
                    variant: 'main',
                  }),
                );
              }}
              variant='ghost'
            >
              <IconLink color={palette.primary.main} size={20} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      renderTopToolbarCustomActions: () => (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              '& .MuiFormHelperText-root': { mx: 0 },
              width: '360px',
            }}
          >
            <DebouncedSearchBar
              ariaLabel={t('chatsTable.search.ariaLabel')}
              onCommit={setSearchQuery}
              onModeChange={mode => setSearchQueryMode(mode as SearchQueryMode)}
              options={searchOptions}
              placeholder={t('chatsTable.search.placeholder', {
                field: searchOptions
                  .find(option => option.value === searchQueryMode)
                  ?.text.toLowerCase(),
              })}
              query={searchQuery}
              selectedMode={searchQueryMode}
              validate={uiQuery => isUiQueryError({ searchQueryMode, uiQuery })}
            />
          </Box>
          <DateRangeFilterButton
            explicitLabel={true}
            initialValue={initialInsightParams.date}
            onChange={value => {
              setDateRange(value);
              const timeStamp = dateRangeToTimestamp(value);
              emitTrackingEventCallback('insight-date-change', {
                from: timeStamp.start_timestamp,
                scope,
                tab,
                table: 'chat',
                to: timeStamp.end_timestamp,
              });
            }}
            options={datePickerRangeOptionsRevamp}
            value={dateRange}
          />
          <Button
            onClick={() => setIsDrawerOpen(true)}
            size='medium'
            startIcon={<TuneIcon />}
            variant='secondary'
          >
            <Typography noWrap variant='font14Medium'>
              {filterCount
                ? t('chatsTable.allFiltersWithCount', { count: filterCount })
                : t('chatsTable.allFilters')}
            </Typography>
          </Button>
          {isHumanEvalsEnabled && (
            <Tooltip tooltipContent={t('chatsTable.flaggedForHumanFeedback')}>
              <FlaggedFilterButton
                $isActive={isFlaggedOnly}
                aria-pressed={isFlaggedOnly}
                onClick={() => {
                  // Shortcut for the "only flagged" selection. Reset to page 1
                  // in the same batched update as the toggle so the table
                  // refetches once. The humanFeedbackKey effect above is only
                  // the fallback for cross-component toggles from the Human
                  // feedback card's "Give feedback" button, where this onClick
                  // doesn't run.
                  setPage(1);
                  setHumanFeedback(isFlaggedOnly ? [] : ['flagged']);
                }}
                size='medium'
                startIcon={
                  <img alt='' height={20} src={flagHeartIcon} width={20} />
                }
                variant='secondary'
              >
                <Typography noWrap variant='font14Medium'>
                  {t('chatsTable.flagged')}
                </Typography>
                {isFlaggedOnly && (
                  <IconCheck
                    color={palette.colors.grey[800]}
                    size={20}
                    style={{ marginLeft: 4 }}
                  />
                )}
              </FlaggedFilterButton>
            </Tooltip>
          )}
          {Boolean(fallbackWorkflowIds.length) && !isVoiceChannel && (
            <Tooltip tooltipContent={t('chatsTable.gapsTooltip')}>
              <Checkbox
                checked={isGapsOnlySelected}
                label={t('chatsTable.gapsOnly')}
                onChange={event => {
                  const isChecked = event.target.checked;
                  if (isChecked) {
                    const updatedMultiSelected = [
                      ...multiSelected,
                      ...fallbackWorkflowIds.map(id => `workflow_id.${id}`),
                    ];
                    setMultiSelected(updatedMultiSelected);
                  } else {
                    const updatedMultiSelected = multiSelected.filter(
                      item =>
                        !fallbackWorkflowIds.some(
                          id => item === `workflow_id.${id}`,
                        ),
                    );
                    setMultiSelected(updatedMultiSelected);
                  }
                }}
              />
            </Tooltip>
          )}
          {areFiltersUsed && (
            <Button
              onClick={handleResetAllFilter}
              size='medium'
              variant='ghost'
            >
              <Typography noWrap variant='font14Medium'>
                {t('chatsTable.resetFilters')}
              </Typography>
            </Button>
          )}
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              justifyContent: 'flex-end',
            }}
          >
            <InsightDownloadCsv
              filename='chats.csv'
              requestData={{ ...tableRequestParams }}
              url={getChatsTableDownloadUrl()}
            />
          </Box>
        </Box>
      ),
    };
  }, [
    t,
    columns,
    rows,
    defaultedSavedTableOptions,
    setSelectedConversationId,
    emitTrackingEventCallback,
    scope,
    tab,
    palette.primary.main,
    dispatch,
    setSavedTableOptions,
    searchOptions,
    searchQuery,
    searchQueryMode,
    setSearchQuery,
    dateRange,
    filterCount,
    fallbackWorkflowIds,
    isVoiceChannel,
    isGapsOnlySelected,
    areFiltersUsed,
    handleResetAllFilter,
    tableRequestParams,
    getChatsTableDownloadUrl,
    setSearchQueryMode,
    setDateRange,
    multiSelected,
    setMultiSelected,
    isHumanEvalsEnabled,
    isFlaggedOnly,
    setHumanFeedback,
    palette.colors.grey,
  ]);

  return (
    <>
      {selectedConversationId && ( // Only render drawer when we have an ID
        <ConversationDrawer
          flaggedConversationIds={flaggedConversationIds}
          isLoading={isLoadingConversation || isFetchingConversation}
          onClose={handleDrawerClose}
          onSelectConversation={setSelectedConversationId}
          openEventTrigger={handleDrawerOpen}
          selectedConversation={selectedConversation}
        />
      )}
      {isDrawerOpen && (
        <GlobalFilterDrawer
          filterType='chat'
          handleReset={handleResetAllFilter}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          otherFiltersOptions={filterOptions}
          preserveFilterOrder={isVoiceChannel || isResolutionUiEnabled}
          searchOptions={searchOptions}
          setUiQuery={setSearchQuery}
          showHumanFeedbackFilter={isHumanEvalsEnabled}
          tab={tab}
          uiQuery={searchQuery}
        />
      )}
      <ChatsTableLayout
        table={
          <InfiniteTable
            hasNextPage={
              typeof conversationTableData?.metadata.next_page === 'number'
            }
            initialSorting={initialSorting}
            isError={isChatsTableError}
            isLoadingFirstPage={showLoadingSkeleton}
            isLoadingNextPage={isConversationTableFetching}
            onLoadMore={handleLoadMore}
            onSortCallback={handleSortCallback}
            stickyHeaderHeight={getTableHeight(view)}
            tableOptions={{
              ...tableOptions,
              initialState: savedTableOptions,
              state: chatsTableControlledState,
            }}
          />
        }
      />
    </>
  );
};

const FlaggedFilterButton = styled(Button, {
  shouldForwardProp: prop => prop !== '$isActive',
})<{ $isActive: boolean }>(({ $isActive, theme }) => ({
  // Render as a white, slate-outlined button in both states (per Figma) instead
  // of the filled secondary variant; the check icon is the only selected
  // indicator. Hover keeps the same resting look (no fill). When active,
  // tighten the right padding since the trailing check needs less room than
  // text (default medium button padding is 8px 16px).
  '&:hover': {
    backgroundColor: theme.palette.colors.white,
    borderColor: theme.palette.colors.slate[200],
  },
  backgroundColor: theme.palette.colors.white,
  borderColor: theme.palette.colors.slate[200],
  paddingRight: $isActive ? '8px' : undefined,
}));

export default ChatsTable;
